// Floor sale, step 2: colour, size and this store's stock for one style.
import React, { useState } from "react";
import { findVariant } from "../../../../utils/fashion/styleIndex";
import { formatMoney } from "../../../../utils/fashion/money";
import { swatchStyle } from "../../../../utils/fashion/colourHex";
import FashionStockPanel from "../FashionStockPanel";
import { stockState } from "./floorHelpers";
import { Back, BagButton, FloorImage, Header } from "./FloorParts";

export default function FloorProduct({ style, tillData, isOnline, count, priceFor, customer, onBack, onBag, onAdd }) {
  const [colour, setColour] = useState(style.colours[0] ?? null);
  // A one-size style has nothing to pick.
  const sizes = style.sizes.length > 0 ? style.sizes : [null];
  const [size, setSize] = useState(sizes.length === 1 ? sizes[0] : undefined);
  const [showStores, setShowStores] = useState(false);

  const picked = size !== undefined;
  const variant = picked ? findVariant(style, colour, size) : null;
  const stock = variant ? stockState(variant) : null;
  const deal = variant ? priceFor?.(variant) : null;
  const marked = deal && deal.price < deal.was;
  const price = variant ? (marked ? deal.price : variant.sunitprice) : style.minPrice;
  const canLookAround = isOnline !== false && Boolean(tillData?.tillAccess?.csBunit?.csBunitId);

  return (
    <>
      <Header>
        <Back onClick={onBack}>Styles</Back>
        <BagButton count={count} onClick={onBag} />
      </Header>
      <div className="fp-fl-body">
        <FloorImage src={style.imageUrl} alt={style.name} className="fp-fl-hero" art={style.art} colour={colour ?? style.colours[0]} />
        <div>
          {style.brandName && <span className="fp-fl-lbl">{style.brandName}</span>}
          <h1 className="fp-fl-disp">{style.name}</h1>
        </div>
        <div className="fp-fl-row">
          <b className="fp-num fp-fl-big">{formatMoney(price, tillData)}</b>
          {marked && <s className="fp-fl-mute fp-num">{formatMoney(deal.was, tillData)}</s>}
        </div>
        {style.colours.length > 0 && (
          <div className="fp-fl-fl" role="group" aria-label="Colour">
            <span className="fp-fl-lbl">Colour · {colour}</span>
            <div className="fp-fl-row">
              {style.colours.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`fp-fl-opt${c === colour ? " is-on" : ""}`}
                  aria-pressed={c === colour}
                  onClick={() => {
                    setColour(c);
                    if (sizes.length > 1) setSize(undefined);
                  }}
                >
                  <span className="fp-sw-dot" style={swatchStyle(c)} aria-hidden="true" />
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}
        {style.sizes.length > 0 && (
          <div className="fp-fl-fl" role="group" aria-label="Size">
            <span className="fp-fl-lbl">Size</span>
            <div className="fp-fl-sizes">
              {sizes.map((s) => {
                const v = findVariant(style, colour, s);
                const st = v ? stockState(v) : null;
                return (
                  <button
                    key={s}
                    type="button"
                    className={`fp-fl-sz${s === size ? " is-on" : ""}${st?.n === 0 ? " is-out" : ""}`}
                    aria-pressed={s === size}
                    disabled={!v}
                    title={v ? undefined : "Not made in this colour"}
                    onClick={() => setSize(s)}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {!picked && <span className="fp-fl-hint">Pick a size to see stock.</span>}
        {stock && <span className={`fp-fl-pill fp-fl-${stock.tone}`}>{stock.text}</span>}
        {canLookAround && (
          <button type="button" className="fp-fl-btn" aria-expanded={showStores} onClick={() => setShowStores((x) => !x)}>
            {showStores ? "Hide other stores" : "Other stores"}
          </button>
        )}
        {/* Once a size is picked, other stores list just that size, one row per store. */}
        {showStores && canLookAround && (
          <FashionStockPanel key={`${colour}|${picked ? size : ""}`} style={style} tillData={tillData} isOnline={isOnline} initialColour={colour} size={picked ? size : undefined} customer={customer} />
        )}
      </div>
      <footer className="fp-fl-ft">
        <button type="button" className="fp-fl-btn fp-fl-pri fp-fl-wide" disabled={!variant} onClick={() => onAdd(variant)}>
          Add to bag
        </button>
      </footer>
    </>
  );
}
